<template>
  <tx-autocomplete
    ref="refAutocomplete"
    :model-value="modelValue"
    :label="label"
    :placeholder="placeholder"
    :debounce="500"
    :errors="errors"
    :fetch-suggestions="searchDatasetValues"
    value-prop="Id"
    display-prop="DisplayValue"
    :required="required"
    :disabled="disabled"
    :clearable="clearable"
    :no-suggestions-message="t('general.messages.startTypingToFetch')"
    @update:model-value="onModelValueUpdated"
  />
</template>

<script setup lang="ts">
import type { ErrorObject } from '@vuelidate/core'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TxAutocomplete from './TxAutocomplete.vue'
import { useUserStore } from '@/store/userData'
import { getDatasetValueById, getDatasetValues } from '@/api/t1/datasets'
import type Article from '@/models/article'
import utils from '@/services/utils'

interface IProps {
  articles: Array<Article | Record<string, any>>
  modelValue?: string | number | null
  errors?: ErrorObject[]
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  clearable?: boolean
  attribute?: IFormEditorAttribute
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: '',
  placeholder: '',
  required: false,
  clearable: false,
  disabled: false,
  errors: () => [] as ErrorObject[],
})

const emit = defineEmits<{
  (e: 'update:modelValue', val: string | number | null): void
  (e: 'change', val: string | number | null): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const refAutocomplete = ref<InstanceType<typeof TxAutocomplete>>()

const lastResults = ref<any[]>([])

function onModelValueUpdated(val?: string | number) {
  // Check if the selected value exists in last search results
  const value = val != null ? val : null

  const found = lastResults.value.find(d => String(d.Id) === String(value))
  if (found) {
    emit('update:modelValue', value)
    emit('change', value)
  }
  else {
    // Invalid input — clear it or do not emit
    emit('update:modelValue', '')
    emit('change', '')
  }
}
watch(() => props.modelValue, async (newVal) => {
  if (refAutocomplete.value) {
    if (newVal === null || newVal === '') {
      refAutocomplete.value.setDisplayModelValue('')
    }
    else {
      // const displayValue: string = ''
      const found = lastResults.value.find(d => String(d.Id) === String(newVal))
      if (found) {
        refAutocomplete.value.setDisplayModelValue(found.DisplayValue)
      }
      else {
        if (userStore.activeCatalog && props.attribute && props.attribute.DatasetId) {
          const data = await getDatasetValueById(userStore.activeCatalog.AccountId, props.attribute.DatasetId, Number(newVal))
          if (data) {
            refAutocomplete.value.setDisplayModelValue(data.DisplayValue)
          }
        }
      }
    }
  }
})

async function searchDatasetValues(query: string, callback: (results: any[]) => void) {
  const trimmed = query.trim()
  if (trimmed.length < 3) {
    lastResults.value = []
    callback([]) // Clear suggestions
    return
  }
  if (!userStore.activeCatalog || !props.attribute?.DatasetId) {
    lastResults.value = []

    callback([])
    return
  }
  try {
    const data = await getDatasetValues(
      userStore.activeCatalog.AccountId,
      props.attribute.DatasetId,
      1,
      100,
      trimmed,
    )
    lastResults.value = data || []
    callback(data || [])
  }
  catch (err) {
    console.error('Error fetching dataset values:', err)
    lastResults.value = []
    callback([])
  }
}

onMounted(async () => {
  if (props.modelValue && props.attribute && props.articles) {
    const article = props.articles[0]
    let displayValue: string = ''
    if (props.attribute._datasetAttributes && props.attribute._datasetAttributes.length && article && utils.isDefined(article[props.attribute.SystemName])) {
      props.attribute._datasetAttributes.forEach((dataAttrSystemName, index) => {
        displayValue += article[`${props.attribute?.SystemName}-${dataAttrSystemName}`]
        displayValue += (props.attribute && props.attribute._datasetAttributes && index !== props.attribute._datasetAttributes.length - 1) ? '-' : ''
      })
    }
    if (refAutocomplete.value) {
      refAutocomplete.value.setDisplayModelValue(displayValue)
    }
  }
})
</script>
